import { useRef, useState } from 'react';
import moment from 'moment';

import Button from 'components/form/Button.component';
import { useTranslation } from 'providers/GILT.provider';
import asArray from 'utils/arrays/asArray.util';

import { AddService } from './AddService';
import { calculateDifferentRoleExecutions, cloneServiceForEdit } from './utils';

import CN from './EditServiceSection.module.scss';

const updateRoleExecution = (roleExecutions, roleId, updates) => roleExecutions.map((role) => (role.role_id === roleId ? { ...role, ...updates } : role));

export const EditServiceSection = ({ service, category, quickSetupLayout, onEditServiceSave }) => {
  const originalServiceRef = useRef(null);
  if (!originalServiceRef.current) {
    originalServiceRef.current = cloneServiceForEdit(service);
  }
  const [editedService, setEditedService] = useState(() => cloneServiceForEdit(service));
  const { t } = useTranslation();

  const selectTax = (taxes) => {
    const existingTaxes = originalServiceRef.current.tax_ids ?? [];
    const newTaxes = [
      ...Object.entries(taxes)
        .filter(([_, value]) => !!value)
        .map(([key]) => parseInt(key, 10)),
    ];
    const finalTaxes = [...newTaxes];
    existingTaxes.forEach((tax) => {
      if (taxes?.[tax] === undefined) {
        finalTaxes.push(tax);
      }
    });
    setEditedService((prev) => ({ ...prev, tax_ids: finalTaxes }));
  };

  const selectFee = (fees) => {
    const existingFees = originalServiceRef.current.fee_ids ?? [];
    const newFees = [
      ...Object.entries(fees)
        .filter(([_, value]) => !!value)
        .map(([key]) => parseInt(key, 10)),
    ];
    const finalFees = [...newFees];
    existingFees.forEach((fee) => {
      if (fees?.[fee] === undefined) {
        finalFees.push(fee);
      }
    });
    setEditedService((prev) => ({ ...prev, fee_ids: finalFees }));
  };

  const toggleExempt = (isExempt) => {
    setEditedService((prev) => ({ ...prev, is_tax_exempt: isExempt, tax_exempt_reason: !isExempt ? '' : prev.tax_exempt_reason ?? '' }));
  };

  const changeReason = (reason) => {
    setEditedService((prev) => ({ ...prev, tax_exempt_reason: reason }));
  };

  const onRolePriceBlur = (value) => {
    setEditedService((prev) => ({ ...prev, price: value }));
  };

  const saveEditedService = () => {
    if (isSaveDisabled || !onEditServiceSave) return;

    const originalRoleIds = asArray(originalServiceRef.current?.role_executions).map(({ role_id: roleId }) => roleId);
    onEditServiceSave(editedService, calculateDifferentRoleExecutions(editedService, originalServiceRef.current), originalRoleIds);
  };

  const changeServiceName = (newName) => setEditedService((prev) => ({ ...prev, name: newName }));

  const changePrice = (newPrice) => setEditedService((prev) => ({ ...prev, price: newPrice }));

  const changeDuration = (newDuration) => {
    const formattedDuration = moment(newDuration, 'HH:mm').format('HH:mm:ss');
    setEditedService((prev) => ({ ...prev, duration: formattedDuration }));
  };

  const changeProcessing = (newProcessing) => {
    const formattedProcessing = moment(newProcessing, 'HH:mm').format('HH:mm:ss');
    setEditedService((prev) => ({ ...prev, processing: formattedProcessing }));
  };

  const changeCategory = (_, newCategory) => setEditedService((prev) => ({ ...prev, category: newCategory.id }));

  const changeVariablePricing = (newVariablePricing) => setEditedService((prev) => ({ ...prev, variable_pricing: newVariablePricing }));

  const changeRoleProcessing = (roleId, newProcessing) => {
    const formattedProcessing = moment(newProcessing, 'HH:mm').format('HH:mm:ss');
    setEditedService((prev) => ({
      ...prev,
      role_executions: updateRoleExecution(prev.role_executions, roleId, { processing: formattedProcessing }),
      processing: newProcessing,
    }));
  };

  // toggle role behavior
  const changeRole = (roleId) => {
    setEditedService((prev) => {
      const existingIndex = prev.role_executions.findIndex((role) => role.role_id === roleId);
      if (existingIndex !== -1) {
        return {
          ...prev,
          role_executions: prev.role_executions.filter((role) => role.role_id !== roleId),
        };
      }
      return {
        ...prev,
        role_executions: [
          ...prev.role_executions,
          {
            role_id: roleId,
            price: '',
            duration: '00:45:00',
            processing: '00:00:00',
          },
        ],
      };
    });
  };

  const changeRolePrice = (roleId, newPrice) => {
    setEditedService((prev) => ({
      ...prev,
      role_executions: updateRoleExecution(prev.role_executions, roleId, { price: newPrice }),
      price: newPrice,
    }));
  };

  const changeRoleApplication = (roleId, newDuration) => {
    const formattedDuration = moment(newDuration, 'HH:mm').format('HH:mm:ss');
    setEditedService((prev) => ({
      ...prev,
      role_executions: updateRoleExecution(prev.role_executions, roleId, { duration: formattedDuration }),
      duration: newDuration,
    }));
  };

  const isSaveDisabled = editedService?.is_tax_exempt === true && !editedService?.tax_exempt_reason?.trim();

  return (
    <div data-bem="EditServiceSection" className={CN.component}>
      <AddService
        service={editedService}
        category={category}
        isNameSelected
        quickSetupLayout={quickSetupLayout}
        serviceTemplates={[]}
        settingsLayout
        onServiceNameChange={changeServiceName}
        onCategoryChangeInSettings={changeCategory}
        onRoleCheckboxChange={changeRole}
        onRolePriceChange={changeRolePrice}
        onRolePriceBlur={onRolePriceBlur}
        onRoleApplicationChange={changeRoleApplication}
        onRoleProcessingChange={changeRoleProcessing}
        onDurationChange={changeDuration}
        onProcessingChange={changeProcessing}
        onPriceChange={changePrice}
        onPrincingChange={changeVariablePricing}
        onSelectTax={selectTax}
        onSelectFee={selectFee}
        onToggleExempt={toggleExempt}
        onChangeReason={changeReason}
      />
      <div className={CN.buttonContainer}>
        <Button xSpace noMargin width="max" variant="primaryForce" disabled={isSaveDisabled} onClick={saveEditedService}>
          {t('common.buttons.saveChanges')}
        </Button>
      </div>
    </div>
  );
};
